import type { CSSSettings, Editable } from "hoxton-types";
import { useStore } from "vuex";

import { useEditor } from "@/composables/useEditor";

import type { RootState } from "@/store/types";
import { CampaignGetters } from "@/store/modules/campaign";
import { EditorAction, EditorGetters } from "@/store/modules/editor";

import { TemplateOverwriteScopes } from "@/enums/overwrites";
import type { EditableOverwrite } from "@/types/types";
import editableUtils from "@/utils/editables";

export function useOverwrite() {
    const store = useStore<RootState>();

    const { selectedGroupValues, selectedGroupValuesIds, selectedLanguage } = useEditor();

    function getAffectingGroupValueIds(groupsAffectingEditable: string[]) {
        if (!groupsAffectingEditable) {
            return [];
        }

        return groupsAffectingEditable.reduce((acc, groupName) => {
            if (groupName in selectedGroupValues.value) {
                const group = selectedGroupValues.value[groupName];
                acc.push(group.groupValueId);
            }

            return acc;
        }, [] as string[]);
    }

    function getOverwrite(editable: Editable, groupsAffectingEditable: string[], templateId: string | null) {
        const relevantSelectedGroups = getAffectingGroupValueIds(groupsAffectingEditable);

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        return store.getters[EditorGetters.editableOverwriteValue](
            editable._id,
            templateId,
            relevantSelectedGroups
        ) as EditableOverwrite;
    }

    function getOverwriteMediaItem(editable: Editable, groupsAffectingEditable: string[], templateId: string | null) {
        return getOverwrite(editable, groupsAffectingEditable, templateId).mediaItem;
    }

    function getSavedOverwrite(
        editableId: string,
        templateId: string | null,
        language?: string,
        selectedGroupsIds?: string[]
    ) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        return store.getters[CampaignGetters.editableOverwriteValue](
            editableId,
            templateId,
            language ?? selectedLanguage.value,
            selectedGroupsIds ?? selectedGroupValuesIds.value
        ) as EditableOverwrite;
    }

    function isTemplateOverwrite(editable: Editable, groupsAffectingEditable: string[], templateId: string) {
        const overwrite = getOverwrite(editable, groupsAffectingEditable, templateId);

        return overwrite && TemplateOverwriteScopes.includes(overwrite.scope);
    }

    async function removeOverwrite(overwriteId: string) {
        await store.dispatch(EditorAction.RemoveOverwrite, overwriteId);
    }

    function shouldRemoveOverwrite(
        editable: Editable,
        masterTemplateId: string | null,
        currentScope: string,
        { value, mediaItem, scope, settings }: EditableOverwrite
    ) {
        if (!settings) {
            return false;
        }
        // Overwrite should be removed if there is no value and every setting matches the default value
        const defaultSettings = editableUtils.getDefaultSettings(editable, masterTemplateId) as CSSSettings;

        return (
            scope === currentScope &&
            value !== "" &&
            !value &&
            !mediaItem &&
            Object.keys(settings).every(
                key =>
                    JSON.stringify(settings[key as keyof CSSSettings]) ===
                    JSON.stringify(defaultSettings[key as keyof CSSSettings])
            )
        );
    }

    return {
        getOverwrite,
        getOverwriteMediaItem,
        getSavedOverwrite,
        removeOverwrite,
        isTemplateOverwrite,
        shouldRemoveOverwrite
    };
}
